/**
 * ArcGIS Pro-style colour dropdown: No color, the generated ArcGIS Colors
 * grid, an eyedropper, a native picker, and hex / transparency properties.
 */

import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { Icon, tokens } from '@/design';
import {
  ARCGIS_COLOR_GRID,
  alphaPercentOf,
  colorLabel,
  normalizeHexColor,
  opaqueHex,
  withAlphaPercent,
} from './symbolColors';

export interface SymbolColorPickerProps {
  /** Field name used for accessible labels, e.g. "Symbol color". */
  readonly label: string;
  readonly value: string | null;
  readonly onChange: (next: string | null) => void;
  readonly allowNoColor?: boolean;
  readonly disabled?: boolean;
}

type EyeDropperCtor = new () => { open(): Promise<{ sRGBHex: string }> };

function eyeDropperCtor(): EyeDropperCtor | null {
  if (typeof window === 'undefined') return null;
  const found = (window as unknown as { EyeDropper?: EyeDropperCtor }).EyeDropper;
  return typeof found === 'function' ? found : null;
}

const CHECKER = `repeating-conic-gradient(${tokens.color.surface[3]} 0% 25%, ${tokens.color.surface[1]} 0% 50%) 0 0 / ${tokens.space[2]}px ${tokens.space[2]}px`;

export function SymbolColorPicker({
  label,
  value,
  onChange,
  allowNoColor = true,
  disabled = false,
}: SymbolColorPickerProps) {
  const [open, setOpen] = useState(false);
  const [showProperties, setShowProperties] = useState(false);
  const [hexDraft, setHexDraft] = useState(opaqueHex(value));
  const rootRef = useRef<HTMLDivElement>(null);
  const eyeDropper = eyeDropperCtor();

  useEffect(() => {
    setHexDraft(opaqueHex(value));
  }, [value]);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    window.addEventListener('mousedown', onPointerDown);
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('mousedown', onPointerDown);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const swatchFill: CSSProperties = value === null
    ? { background: CHECKER }
    : { background: value };

  const row: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.space[2],
    width: '100%',
    minHeight: tokens.density.rowDense,
    padding: `0 ${tokens.space[2]}px`,
    border: 0,
    borderRadius: tokens.radius.sm,
    background: 'transparent',
    color: tokens.color.ink.primary,
    fontFamily: tokens.font.family.ui,
    fontSize: tokens.font.size.sm,
    textAlign: 'left',
    cursor: 'pointer',
  };

  return (
    <div ref={rootRef} style={{ position: 'relative' }}>
      <button
        type="button"
        aria-label={label}
        aria-haspopup="dialog"
        aria-expanded={open}
        disabled={disabled}
        data-color={value ?? 'none'}
        onClick={() => setOpen((previous) => !previous)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: tokens.space[1],
          width: '100%',
          minHeight: tokens.density.rowDense,
          padding: `0 ${tokens.space[1]}px`,
          border: tokens.border.thin,
          borderRadius: tokens.radius.input,
          background: tokens.color.surface[0],
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.5 : 1,
        }}
      >
        <span
          aria-hidden
          style={{
            flex: 1,
            height: tokens.space[4],
            border: tokens.border.thin,
            borderRadius: tokens.radius.sm,
            ...swatchFill,
          }}
        />
        <Icon name={open ? 'expand_less' : 'expand_more'} size={16} decorative />
      </button>

      {open ? (
        <div
          role="dialog"
          aria-label={`${label} palette`}
          data-testid="symbol-color-palette"
          style={{
            position: 'absolute',
            zIndex: tokens.zIndex.tooltip,
            top: `calc(100% + ${tokens.space[1]}px)`,
            right: 0,
            width: tokens.layout.popoverSm,
            padding: tokens.space[2],
            background: tokens.color.surface[1],
            border: tokens.border.thin,
            borderRadius: tokens.radius.md,
            boxShadow: tokens.elevation[2],
          }}
        >
          {allowNoColor ? (
            <button
              type="button"
              aria-label={`${label}: no color`}
              onClick={() => {
                onChange(null);
                setOpen(false);
              }}
              style={row}
            >
              <span
                aria-hidden
                style={{
                  width: tokens.space[4],
                  height: tokens.space[4],
                  border: tokens.border.thin,
                  background: CHECKER,
                }}
              />
              No color
            </button>
          ) : null}

          <div
            style={{
              margin: `${tokens.space[1]}px 0`,
              padding: `0 ${tokens.space[2]}px`,
              color: tokens.color.ink.muted,
              fontSize: tokens.font.size.xs,
            }}
          >
            ArcGIS Colors
          </div>
          <div
            role="group"
            aria-label="ArcGIS Colors"
            style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${ARCGIS_COLOR_GRID[0].length}, 1fr)`,
              gap: tokens.border.width.thin * 2,
              padding: `0 ${tokens.space[1]}px ${tokens.space[2]}px`,
            }}
          >
            {ARCGIS_COLOR_GRID.flat().map((swatch) => {
              const selected = opaqueHex(value) === swatch.hex && value !== null;
              return (
                <button
                  key={swatch.name}
                  type="button"
                  title={`${swatch.name} ${swatch.hex.toUpperCase()}`}
                  aria-label={`${label}: ${swatch.name}`}
                  aria-pressed={selected}
                  onClick={() => {
                    onChange(withAlphaPercent(swatch.hex, alphaPercentOf(value)));
                    setOpen(false);
                  }}
                  style={{
                    aspectRatio: '1',
                    padding: 0,
                    background: swatch.hex,
                    border: selected
                      ? `${tokens.focus.ringWidth}px solid ${tokens.color.accent.primary}`
                      : tokens.border.thin,
                    cursor: 'pointer',
                  }}
                />
              );
            })}
          </div>

          <button
            type="button"
            aria-label={`${label}: pick with eyedropper`}
            disabled={!eyeDropper}
            title={eyeDropper ? 'Eyedropper' : 'Eyedropper needs a Chromium browser'}
            onClick={async () => {
              if (!eyeDropper) return;
              try {
                const result = await new eyeDropper().open();
                const picked = normalizeHexColor(result.sRGBHex);
                if (picked) onChange(withAlphaPercent(picked, alphaPercentOf(value)));
                setOpen(false);
              } catch {
                // The user dismissed the picker; keep the current colour.
              }
            }}
            style={{ ...row, opacity: eyeDropper ? 1 : 0.5 }}
          >
            <Icon name="colorize" size={16} decorative />
            Eyedropper
          </button>

          <label
            style={{
              ...row,
              borderTop: tokens.border.thin,
              borderRadius: 0,
              cursor: 'pointer',
            }}
          >
            <span style={{ flex: 1 }}>More colors…</span>
            <input
              type="color"
              aria-label={`${label} custom value`}
              value={hexDraft}
              onChange={(event) =>
                onChange(withAlphaPercent(event.target.value, alphaPercentOf(value)))
              }
              style={{
                width: tokens.space[6],
                height: tokens.space[5],
                padding: 0,
                border: tokens.border.thin,
                background: 'transparent',
              }}
            />
          </label>

          <button
            type="button"
            aria-expanded={showProperties}
            onClick={() => setShowProperties((previous) => !previous)}
            style={{ ...row, borderTop: tokens.border.thin, borderRadius: 0 }}
          >
            Color properties…
          </button>

          {showProperties ? (
            <div
              style={{
                display: 'grid',
                gap: tokens.space[2],
                padding: tokens.space[2],
              }}
            >
              <label style={{ display: 'grid', gap: tokens.space[1] }}>
                <span style={{ fontSize: tokens.font.size.xs, color: tokens.color.ink.muted }}>
                  Hex
                </span>
                <input
                  aria-label={`${label} hex`}
                  value={hexDraft}
                  onChange={(event) => setHexDraft(event.target.value)}
                  onBlur={() => {
                    const parsed = normalizeHexColor(hexDraft);
                    if (parsed) {
                      onChange(withAlphaPercent(parsed, alphaPercentOf(value)));
                    } else {
                      setHexDraft(opaqueHex(value));
                    }
                  }}
                  style={{
                    minHeight: tokens.density.rowDense,
                    padding: `0 ${tokens.space[2]}px`,
                    border: tokens.border.thin,
                    borderRadius: tokens.radius.input,
                    background: tokens.color.surface[0],
                    color: tokens.color.ink.primary,
                    fontFamily: tokens.font.family.mono,
                    fontSize: tokens.font.size.sm,
                    textTransform: 'uppercase',
                  }}
                />
              </label>
              <label style={{ display: 'grid', gap: tokens.space[1] }}>
                <span style={{ fontSize: tokens.font.size.xs, color: tokens.color.ink.muted }}>
                  Transparency {100 - alphaPercentOf(value)}%
                </span>
                <input
                  aria-label={`${label} transparency`}
                  type="range"
                  min={0}
                  max={100}
                  value={100 - alphaPercentOf(value)}
                  onChange={(event) =>
                    onChange(
                      withAlphaPercent(
                        opaqueHex(value),
                        100 - Number(event.target.value),
                      ),
                    )
                  }
                />
              </label>
            </div>
          ) : null}

          <div
            aria-live="polite"
            style={{
              padding: `${tokens.space[1]}px ${tokens.space[2]}px`,
              color: tokens.color.ink.secondary,
              fontSize: tokens.font.size.xs,
            }}
          >
            {colorLabel(value)}
          </div>
        </div>
      ) : null}
    </div>
  );
}
